import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Upload, FileText, Check, X, ExternalLink, Search, AlertTriangle, Loader2, Building2, Cpu, UserPlus, Plus, Trash2, ShieldCheck } from 'lucide-react';
import Modal from '../Modal';
import Button from '../Button';
import { aiApi, aiUpload, formatScore, scoreColor, AI_ACCEPT } from '../../utils/aiApi';
import { MACHINE_TYPES } from '../../constants';

// Smart document intake: upload a PDF/photo, Claude reads it, the ERP suggests the customer or machine,
// the user checks the suggestion and saves the file as a regular ERP document.

const DOC_TYPES = [
  ['contract', 'Smlouva'],
  ['invoice', 'Faktura'],
  ['handover_protocol', 'Předávací protokol'],
  ['order', 'Objednávka'],
  ['offer', 'Nabídka'],
  ['certificate', 'Certifikát'],
  ['technical', 'Technická dokumentace'],
  ['other', 'Ostatní'],
];
const DOC_TYPE_LABEL = Object.fromEntries(DOC_TYPES);

// customer column -> [key in extraction.customer, Czech label]; same whitelist as the server.
const FILL_FIELDS = {
  ico: ['ico', 'IČO'],
  dic: ['dic', 'DIČ'],
  street: ['street', 'Ulice'],
  city: ['city', 'Město'],
  postal_code: ['postalCode', 'PSČ'],
  phone: ['phone', 'Telefon'],
  email: ['email', 'E-mail'],
  proxy_name: ['personName', 'Zástupce'],
};

const ACCENT = '#E53935';
const BORDER = '#E0E0E0';

const s = {
  panel: { background: '#fff', borderRadius: '12px', maxWidth: '720px', width: 'calc(100% - 32px)', maxHeight: '90vh', overflow: 'auto', boxSizing: 'border-box', fontSize: '14px', color: '#333' },
  header: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '20px 20px 12px', borderBottom: `1px solid ${BORDER}` },
  body: { padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  card: { border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '14px' },
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: `1px solid ${BORDER}`, borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' },
  muted: { fontSize: '13px', color: '#777' },
  badgeAi: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600, color: ACCENT, background: '#FFEBEE', borderRadius: '999px', padding: '2px 8px' },
  error: { display: 'flex', gap: '8px', alignItems: 'flex-start', background: '#FFEBEE', color: '#B71C1C', borderRadius: '8px', padding: '10px 12px', fontSize: '13px' },
  row: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' },
};

const spinStyle = { animation: 'aiIntakeSpin 1s linear infinite' };
const SpinKeyframes = () => <style>{'@keyframes aiIntakeSpin { to { transform: rotate(360deg); } } @media (prefers-reduced-motion: reduce) { [data-ai-spin] { animation: none !important; } }'}</style>;

const customerName = (c) => c.company_name || c.school_name || `Zákazník ${c.id}`;
const machineName = (m) => [m.model_type, m.serial_number || 'bez výr. čísla', m.customer_name].filter(Boolean).join(' · ');

function fillFor(extraction, customer) {
  const fill = {};
  if (!customer || !extraction?.customer) return fill;
  Object.entries(FILL_FIELDS).forEach(([column, [key]]) => {
    const value = extraction.customer[key];
    const own = customer[column];
    if (value && (own === null || own === undefined || String(own).trim() === '')) fill[column] = value;
  });
  return fill;
}

function ErrorBox({ message }) {
  if (!message) return null;
  return <div role="alert" style={s.error}><AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '1px' }} /> <span>{message}</span></div>;
}

function ScoreBadge({ score }) {
  if (score === null || score === undefined) {
    return <span style={{ fontSize: '12px', fontWeight: 600, color: '#555', background: '#F5F5F5', borderRadius: '999px', padding: '2px 8px', whiteSpace: 'nowrap' }}>z karty</span>;
  }
  const color = scoreColor(score);
  return <span title="Jistota párování" style={{ fontSize: '12px', fontWeight: 700, color: '#fff', background: color, borderRadius: '999px', padding: '2px 8px', whiteSpace: 'nowrap' }}>{formatScore(score)}</span>;
}

function OptionRow({ name, checked, onSelect, label, score, reasons, showScore = true }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: `1px solid ${checked ? ACCENT : BORDER}`, background: checked ? '#FFF5F5' : '#fff', borderRadius: '10px', cursor: 'pointer' }}>
      <input type="radio" name={name} checked={checked} onChange={onSelect} style={{ accentColor: ACCENT, margin: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 600, overflowWrap: 'anywhere' }}>{label}</span>
        {reasons?.length > 0 && <span style={{ ...s.muted, fontSize: '12px' }}>Shoda: {reasons.join(', ')}</span>}
      </span>
      {showScore && <ScoreBadge score={score} />}
    </label>
  );
}

// Step 1: choose or drop the file.
function UploadStep({ file, setFile, analyzing, error, onAnalyze, onClose }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };
  return (
    <div style={s.body}>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', textAlign: 'center', padding: '28px 16px', border: `2px dashed ${dragOver ? ACCENT : BORDER}`, borderRadius: '12px', background: dragOver ? '#FFF5F5' : '#FAFAFA', cursor: analyzing ? 'wait' : 'pointer' }}
      >
        <Upload size={28} color={ACCENT} />
        <span style={{ fontWeight: 600, fontSize: '15px' }}>{file ? file.name : 'Přetáhněte sem PDF nebo fotku dokumentu'}</span>
        <span style={s.muted}>{file ? `${Math.max(1, Math.round(file.size / 1024))} kB · klikněte pro jiný soubor` : 'nebo klikněte a vyberte soubor či vyfoťte dokument (PDF do 20 MB, fotka JPG, PNG, WEBP – velké fotky se zmenší)'}</span>
        <input ref={inputRef} type="file" accept={AI_ACCEPT} disabled={analyzing} style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) setFile(e.target.files[0]); e.target.value = ''; }} />
      </label>
      <p style={{ ...s.muted, margin: 0 }}>Claude přečte typ dokumentu, datum, zákazníka, výrobní číslo trenažéru a hlavní údaje. ERP pak navrhne, kam dokument patří.</p>
      {analyzing && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#555', fontSize: '14px' }}>
          <Loader2 size={18} color={ACCENT} style={spinStyle} data-ai-spin /> Claude čte dokument…
        </div>
      )}
      <ErrorBox message={error} />
      <div style={s.footer}>
        <Button variant="secondary" onClick={onClose} disabled={analyzing}>Zrušit</Button>
        <Button icon={Sparkles} onClick={onAnalyze} loading={analyzing} disabled={!file}>{analyzing ? 'Analyzuji…' : 'Analyzovat'}</Button>
      </div>
    </div>
  );
}

function ExtractionSummary({ analysis }) {
  const e = analysis.extraction;
  const c = e.customer || {};
  const m = e.machine || {};
  const details = [
    ['Typ', DOC_TYPE_LABEL[e.documentType] || e.documentType],
    ['Datum', e.documentDate ? new Date(`${e.documentDate}T00:00:00`).toLocaleDateString('cs-CZ') : null],
    ['Zákazník', [c.companyName, c.ico && `IČO ${c.ico}`, c.city].filter(Boolean).join(', ') || null],
    ['Trenažér', [m.modelType, m.serialNumber && `v. č. ${m.serialNumber}`].filter(Boolean).join(', ') || null],
    ['Částka', e.amounts?.total != null ? `${e.amounts.total.toLocaleString('cs-CZ')} ${e.amounts.currency || ''}`.trim() : null],
  ].filter(([, v]) => v);
  return (
    <div style={s.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <FileText size={18} color={ACCENT} />
        <span style={{ fontWeight: 600, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{analysis.fileName}</span>
        <a href={`/api/ai/documents/${analysis.token}/file`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#1976D2', textDecoration: 'none' }}>
          Náhled <ExternalLink size={13} />
        </a>
      </div>
      {e.summary && <p style={{ margin: '0 0 10px', fontSize: '14px', lineHeight: 1.5 }}>{e.summary}</p>}
      <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(80px, max-content) 1fr', gap: '4px 12px', margin: 0, fontSize: '13px' }}>
        {[...details, ...(e.facts || []).map((f) => [f.label, f.value])].map(([k, v], i) => (
          <React.Fragment key={`${k}-${i}`}>
            <dt style={{ color: '#777' }}>{k}</dt>
            <dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

// Manual fallback: search customers by name / IČO / city.
function CustomerSearch({ onPick }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const search = async () => {
    if (!query.trim()) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/api/customers?search=${encodeURIComponent(query.trim())}&limit=10`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Hledání se nezdařilo.');
      setResults(json.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          style={{ ...s.input, minWidth: 0 }} value={query} placeholder="Název, IČO nebo město…" aria-label="Hledat zákazníka"
          onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
        />
        <Button variant="secondary" size="sm" icon={Search} onClick={search} loading={busy}>Hledat</Button>
      </div>
      <ErrorBox message={error} />
      {results && results.length === 0 && <span style={s.muted}>Nic nenalezeno.</span>}
      {results && results.map((c) => (
        <button key={c.id} type="button" onClick={() => { onPick(c); setResults(null); setQuery(''); }} style={{ textAlign: 'left', padding: '8px 12px', border: `1px solid ${BORDER}`, borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit' }}>
          <strong>{customerName(c)}</strong>{(c.ico || c.city) && <span style={s.muted}> · {[c.ico && `IČO ${c.ico}`, c.city].filter(Boolean).join(', ')}</span>}
        </button>
      ))}
    </div>
  );
}


// Machines of the chosen customer (manual fallback when the suggestion is wrong).
function CustomerMachines({ customerId, machineId, onPick }) {
  const [state, setState] = useState({ for: null, items: [], error: '' });
  useEffect(() => {
    if (!customerId) return undefined;
    let alive = true;
    const failed = { for: customerId, items: [], error: 'Trenažéry se nepodařilo načíst.' };
    fetch(`/api/machines?customer_id=${customerId}`, { credentials: 'include' })
      .then((res) => res.json().then((json) => ({ ok: res.ok, json })))
      .then(({ ok, json }) => { if (alive) setState(ok ? { for: customerId, items: json.data || [], error: '' } : { ...failed, error: json.error || failed.error }); })
      .catch(() => { if (alive) setState(failed); });
    return () => { alive = false; };
  }, [customerId]);

  if (!customerId) return <span style={s.muted}>Vyberte nejdřív zákazníka, zobrazí se jeho trenažéry.</span>;
  if (state.for !== customerId) return <span style={s.muted}>Načítám trenažéry…</span>;
  if (state.error) return <ErrorBox message={state.error} />;
  if (!state.items.length) return <span style={s.muted}>Zákazník nemá žádný trenažér.</span>;
  return state.items.map((m) => (
    <OptionRow key={m.id} name="ai-intake-machine" checked={machineId === m.id} onSelect={() => onPick(m)} label={machineName(m)} showScore={false} />
  ));
}

// Fields of a new customer created from the document (same names as the customers table).
// [column, label, flex, maxLength] – lengths as the server keeps them (services/ai/contractSetup.js).
const NEW_CUSTOMER_FIELDS = [
  ['company_name', 'Název firmy / autoškoly', '1 1 100%', 255],
  ['ico', 'IČO', '1 1 140px', 12],
  ['dic', 'DIČ', '1 1 140px', 20],
  ['street', 'Ulice a č. p.', '2 1 220px', 255],
  ['city', 'Město', '1 1 160px', 120],
  ['postal_code', 'PSČ', '0 1 100px', 20],
  ['proxy_name', 'Zástupce / jednatel', '1 1 200px', 255],
  ['phone', 'Telefon', '1 1 160px', 50],
  ['email', 'E-mail', '1 1 200px', 200],
];

const ARES_STATUS = {
  found: { color: '#2E7D32', bg: '#E8F5E9', text: 'Údaje ověřeny v ARES podle IČO' },
  not_found: { color: '#8D6E00', bg: '#FFF8E1', text: 'IČO v ARES nenalezeno – údaje jsou ze smlouvy, zkontrolujte je' },
  unavailable: { color: '#8D6E00', bg: '#FFF8E1', text: 'ARES je nedostupný – údaje jsou ze smlouvy, zkontrolujte je' },
  no_ico: { color: '#8D6E00', bg: '#FFF8E1', text: 'Smlouva neobsahuje IČO – údaje jsou ze smlouvy' },
};

function NewCustomerForm({ value, onChange, proposal }) {
  const status = ARES_STATUS[proposal?.aresStatus] || ARES_STATUS.no_ico;
  const kraj = proposal?.ico && proposal.ico === value.ico ? proposal.geo?.nazev_kraje : null;
  return (
    <div style={{ ...s.card, display: 'flex', flexDirection: 'column', gap: '10px', background: '#FAFAFA' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', alignSelf: 'flex-start', fontSize: '12px', fontWeight: 600, color: status.color, background: status.bg, borderRadius: '999px', padding: '3px 10px' }}>
        <ShieldCheck size={13} aria-hidden="true" /> {status.text}
      </span>
      {proposal?.trashedCustomer && (
        <ErrorBox message={`Zákazník s tímto IČO je v koši (${proposal.trashedCustomer.label}). Obnovte ho raději v administraci, nový by byl duplicitní.`} />
      )}
      <div style={s.row}>
        {NEW_CUSTOMER_FIELDS.map(([field, label, flex, maxLength]) => (
          <div key={field} style={{ flex, minWidth: 0 }}>
            <label style={s.label} htmlFor={`ai-new-${field}`}>{label}</label>
            <input
              id={`ai-new-${field}`} style={s.input} value={value[field] || ''} maxLength={maxLength}
              inputMode={field === 'ico' || field === 'postal_code' ? 'numeric' : field === 'phone' ? 'tel' : undefined}
              onChange={(e) => onChange({ ...value, [field]: e.target.value })}
            />
          </div>
        ))}
      </div>
      {kraj && <span style={s.muted}>Kraj podle ARES: {kraj}</span>}
    </div>
  );
}

const formatDeadline = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d ? `${Number(d)}. ${Number(m)}. ${y}` : iso;
};

const MODEL_OPTIONS = MACHINE_TYPES.map((t) => t.value);
const MAX_UNITS = 20;
// The quantity input may be empty while typing; it is clamped to 1..MAX_UNITS on blur and before saving.
const clampUnits = (value) => Math.max(1, Math.min(MAX_UNITS, Number.parseInt(value, 10) || 1));

const DEADLINE_SOURCE = {
  contract: 'termín plnění uvedený ve smlouvě',
  contract_date: '2 měsíce od podpisu smlouvy',
  today: '2 měsíce od dneška (smlouva nemá datum)',
};
const SITE_FIELDS = [
  ['name', 'Název místa (např. Pobočka Kladno)', '1 1 100%', 255],
  ['street', 'Ulice a č. p.', '2 1 220px', 255],
  ['city', 'Město', '1 1 160px', 120],
  ['postal_code', 'PSČ', '0 1 100px', 20],
];

function InstallationSite({ enabled, onToggle, value, onChange, fromDocument }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: `1px solid ${BORDER}`, paddingTop: '10px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} style={{ accentColor: ACCENT, width: '16px', height: '16px', margin: 0 }} />
        <span>Trenažér bude stát jinde než v sídle (kontaktní adresa / provozovna)</span>
      </label>
      {enabled && (
        <>
          {fromDocument && <span style={s.muted}>Adresu našel Claude ve smlouvě – zkontrolujte ji.</span>}
          <div style={s.row}>
            {SITE_FIELDS.map(([field, label, flex, maxLength]) => (
              <div key={field} style={{ flex, minWidth: 0 }}>
                <label style={s.label} htmlFor={`ai-site-${field}`}>{label}</label>
                <input id={`ai-site-${field}`} style={s.input} value={value[field] || ''} maxLength={maxLength}
                  inputMode={field === 'postal_code' ? 'numeric' : undefined}
                  onChange={(e) => onChange({ ...value, [field]: e.target.value })} />
              </div>
            ))}
          </div>
          <span style={s.muted}>Založí se jako provozovna zákazníka, trenažéry se k ní přiřadí a na Mapě klientů budou na této adrese.</span>
        </>
      )}
    </div>
  );
}

function MachinesToCreate({ enabled, onToggle, items, onChange, deadline, onDeadline, deadlineSource, site }) {
  const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const total = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
  return (
    <fieldset style={{ ...s.card, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <legend style={{ ...s.label, padding: '0 4px', marginBottom: 0 }}>Trenažéry ze smlouvy</legend>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} style={{ accentColor: ACCENT, width: '16px', height: '16px', margin: 0 }} />
        <span>Založit trenažéry{enabled && total ? ` (${total} ks)` : ''} a přiložit k nim smlouvu</span>
      </label>
      {enabled && (
        <>
          {items.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select aria-label="Typ trenažéru" style={{ ...s.input, flex: '1 1 160px', width: 'auto' }} value={it.modelType} onChange={(e) => update(i, { modelType: e.target.value })}>
                {MODEL_OPTIONS.map((m) => <option key={m} value={m}>Autom {m}</option>)}
              </select>
              <input
                aria-label="Počet kusů" type="number" min={1} max={MAX_UNITS} style={{ ...s.input, flex: '0 0 84px', width: '84px' }}
                value={it.quantity}
                onChange={(e) => update(i, { quantity: e.target.value.replace(/[^0-9]/g, '').slice(0, 2) })}
                onBlur={() => update(i, { quantity: clampUnits(it.quantity) })}
              />
              <span style={s.muted}>ks</span>
              <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} aria-label="Odebrat řádek" style={{ padding: '8px', background: '#FFEBEE', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex' }}>
                <Trash2 size={15} color={ACCENT} />
              </button>
            </div>
          ))}
          <Button variant="ghost" size="sm" icon={Plus} onClick={() => onChange([...items, { modelType: 'TR3', quantity: 1 }])}>Přidat typ</Button>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', alignItems: 'flex-end' }}>
            <div style={{ flex: '0 1 200px' }}>
              <label style={s.label} htmlFor="ai-intake-deadline">Doručit do (termín plnění)</label>
              <input id="ai-intake-deadline" type="date" style={s.input} value={deadline} onChange={(e) => onDeadline(e.target.value)} />
            </div>
            <span style={{ ...s.muted, flex: '1 1 200px', paddingBottom: '10px' }}>
              {DEADLINE_SOURCE[deadlineSource] ? `Navrženo: ${DEADLINE_SOURCE[deadlineSource]}.` : ''} Připomínka se ukáže u trenažéru a na dashboardu.
            </span>
          </div>
          <span style={s.muted}>Dostanou nová výrobní čísla, stav „Neobjednáno“ a datum podpisu smlouvy.</span>
          {site}
        </>
      )}
    </fieldset>
  );
}

function TargetTypeSwitch({ value, onChange, showNew }) {
  const option = (type, Icon, label) => (
    <button
      type="button" role="radio" aria-checked={value === type} onClick={() => onChange(type)}
      style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '9px 10px', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', background: value === type ? '#fff' : 'transparent', color: value === type ? ACCENT : '#666', boxShadow: value === type ? '0 1px 3px rgba(0,0,0,0.12)' : 'none' }}
    >
      <Icon size={16} /> {label}
    </button>
  );
  return (
    <div role="radiogroup" aria-label="Kam dokument uložit" style={{ display: 'flex', gap: '4px', background: '#F5F5F5', borderRadius: '10px', padding: '4px', flexWrap: 'wrap' }}>
      {showNew && option('new_customer', UserPlus, 'Nový zákazník')}
      {option('customer', Building2, 'Zákazník')}
      {option('machine', Cpu, 'Trenažér')}
    </div>
  );
}

function CustomerTarget({ suggestions, extra, customerId, onPick }) {
  const list = extra && !suggestions.some((c) => c.id === extra.id) ? [...suggestions, extra] : suggestions;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {list.length === 0 && <span style={s.muted}>Claude v dokumentu nenašel známého zákazníka – vyhledejte ho ručně.</span>}
      {list.map((c) => (
        <OptionRow key={c.id} name="ai-intake-customer" checked={customerId === c.id} onSelect={() => onPick(c)} label={c.label} score={c.score} reasons={c.reasons} showScore={c.manual !== true} />
      ))}
      <CustomerSearch onPick={(row) => onPick({ id: row.id, label: customerName(row), row, manual: true })} />
    </div>
  );
}

function MachineTarget({ suggestions, machineId, customerId, customerLabel, onPickMachine, onPickCustomer }) {
  const [manual, setManual] = useState(suggestions.length === 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {suggestions.length === 0 && <span style={s.muted}>Claude nenašel výrobní číslo známého trenažéru – vyberte ho ručně.</span>}
      {suggestions.map((m) => (
        <OptionRow key={m.id} name="ai-intake-machine" checked={machineId === m.id} onSelect={() => onPickMachine({ id: m.id, label: m.label, customerId: m.customerId })} label={m.label} score={m.score} reasons={m.reasons} />
      ))}
      {!manual && <Button variant="ghost" size="sm" icon={Search} onClick={() => setManual(true)}>Vybrat jiný trenažér</Button>}
      {manual && (
        <div style={{ ...s.card, display: 'flex', flexDirection: 'column', gap: '8px', background: '#FAFAFA' }}>
          <span style={s.label}>Trenažéry zákazníka{customerLabel ? `: ${customerLabel}` : ''}</span>
          <CustomerMachines customerId={customerId} machineId={machineId} onPick={(m) => onPickMachine({ id: m.id, label: machineName(m), customerId: m.customer_id })} />
          <CustomerSearch onPick={(row) => onPickCustomer({ id: row.id, label: customerName(row), row, manual: true })} />
        </div>
      )}
    </div>
  );
}

function CustomerFill({ fill, customerLabel, checked, onToggle }) {
  const fields = Object.keys(fill);
  if (!fields.length) return null;
  return (
    <fieldset style={{ ...s.card, margin: 0 }}>
      <legend style={{ ...s.label, padding: '0 4px', marginBottom: 0 }}>Doplnit chybějící údaje zákazníka</legend>
      <p style={{ ...s.muted, margin: '0 0 8px' }}>{customerLabel ? `${customerLabel}: ` : ''}vyplní se jen prázdná pole, nic se nepřepíše.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {fields.map((f) => (
          <label key={f} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
            <input type="checkbox" checked={checked.includes(f)} onChange={() => onToggle(f)} style={{ accentColor: ACCENT, width: '16px', height: '16px', margin: 0 }} />
            <span style={{ color: '#777', minWidth: '70px' }}>{FILL_FIELDS[f][1]}</span>
            <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{fill[f]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

const suggestionOf = (list, id) => list.find((x) => x.id === id) || null;

// Step 2: check what Claude read, pick the target, save.
function ReviewStep({ analysis, onBack, onSaved }) {
  const { extraction, suggestedTarget } = analysis;
  const [title, setTitle] = useState(extraction.title || '');
  const [docType, setDocType] = useState(extraction.documentType || 'other');
  const [description, setDescription] = useState(extraction.summary || '');
  const [targetType, setTargetType] = useState(suggestedTarget.type);
  const [customer, setCustomer] = useState(() => {
    const found = suggestionOf(analysis.customers, suggestedTarget.customerId);
    return found ? { id: found.id, label: found.label, fill: found.fill || {} } : null;
  });
  const [extraCustomer, setExtraCustomer] = useState(null);
  const [machine, setMachine] = useState(() => {
    const found = suggestionOf(analysis.machines, suggestedTarget.machineId);
    return found ? { id: found.id, label: found.label, customerId: found.customerId } : null;
  });
  const [checked, setChecked] = useState({ customerId: null, fields: [] });
  const [newCustomer, setNewCustomer] = useState(() => {
    const p = analysis.newCustomer || {};
    return Object.fromEntries(NEW_CUSTOMER_FIELDS.map(([f]) => [f, p[f] || '']));
  });
  const proposedMachines = analysis.newMachines || [];
  const [createMachines, setCreateMachines] = useState(Boolean(analysis.createMachinesByDefault));
  const [machineItems, setMachineItems] = useState(proposedMachines.length ? proposedMachines : [{ modelType: 'TR3', quantity: 1 }]);
  const [deadline, setDeadline] = useState(analysis.deliveryDeadline?.date || '');
  const [siteEnabled, setSiteEnabled] = useState(Boolean(analysis.installationSite));
  const [site, setSite] = useState(() => Object.fromEntries(
    ['name', 'street', 'city', 'postal_code'].map((f) => [f, analysis.installationSite?.[f] || '']),
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const pickCustomer = (c) => {
    const next = { id: c.id, label: c.label, fill: c.row ? fillFor(extraction, c.row) : c.fill || {} };
    setCustomer(next);
    if (c.manual) setExtraCustomer({ ...next, score: null, reasons: [], manual: true });
  };
  const pickMachine = (m) => {
    setMachine(m);
    if (m.customerId && m.customerId !== customer?.id) {
      const known = suggestionOf(analysis.customers, m.customerId);
      setCustomer(known ? { id: known.id, label: known.label, fill: known.fill || {} } : { id: m.customerId, label: null, fill: {} });
    }
  };

  // Fields are applied to the customer the document ends up with (for a machine: its owner).
  const fillCustomer = targetType === 'customer' ? customer : (machine && customer && machine.customerId === customer.id ? customer : null);
  const fill = fillCustomer?.fill || {};
  const fieldsChecked = checked.customerId === fillCustomer?.id ? checked.fields.filter((f) => f in fill) : [];
  const toggle = (f) => setChecked({ customerId: fillCustomer.id, fields: fieldsChecked.includes(f) ? fieldsChecked.filter((x) => x !== f) : [...fieldsChecked, f] });

  const isNew = targetType === 'new_customer';
  const targetId = targetType === 'customer' ? customer?.id : targetType === 'machine' ? machine?.id : null;
  const canCreateMachines = targetType !== 'machine';
  const ready = isNew ? Boolean(newCustomer.company_name.trim()) : Boolean(targetId);
  const save = async () => {
    if (!title.trim()) { setError('Zadejte název dokumentu.'); return; }
    if (!ready) { setError(isNew ? 'Vyplňte název nového zákazníka.' : targetType === 'customer' ? 'Vyberte zákazníka.' : 'Vyberte trenažér.'); return; }
    const withMachines = canCreateMachines && createMachines;
    const withSite = withMachines && siteEnabled;
    if (withSite && !site.city.trim() && !site.street.trim()) { setError('U místa instalace vyplňte alespoň město nebo ulici.'); return; }
    setSaving(true);
    setError('');
    try {
      const result = await aiApi('/documents/commit', {
        method: 'POST',
        body: {
          token: analysis.token,
          target: isNew ? { type: 'new_customer' } : { type: targetType, id: targetId },
          title: title.trim(),
          documentType: docType,
          description: targetType === 'machine' || (canCreateMachines && createMachines) ? description : undefined,
          applyCustomerFields: isNew ? [] : fieldsChecked,
          newCustomer: isNew ? newCustomer : undefined,
          createMachines: withMachines
            ? machineItems.map((it) => ({ modelType: it.modelType, quantity: clampUnits(it.quantity) }))
            : undefined,
          deliveryDeadline: withMachines && deadline ? deadline : undefined,
          installationSite: withSite ? site : undefined,
        },
      });
      const label = isNew ? newCustomer.company_name.trim() : targetType === 'customer' ? customer?.label : machine?.label;
      onSaved(result, label, targetType);
    } catch (err) {
      setError(err.message || 'Dokument se nepodařilo uložit.');
      setSaving(false);
    }
  };

  return (
    <div style={s.body}>
      <ExtractionSummary analysis={analysis} />

      <div style={s.row}>
        <div style={{ flex: '2 1 260px' }}>
          <label style={s.label} htmlFor="ai-intake-title">Název dokumentu</label>
          <input id="ai-intake-title" style={s.input} value={title} maxLength={255} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={s.label} htmlFor="ai-intake-type">Typ</label>
          <select id="ai-intake-type" style={s.input} value={docType} onChange={(e) => setDocType(e.target.value)}>
            {DOC_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <span style={s.label}>Kam dokument patří</span>
        <TargetTypeSwitch value={targetType} onChange={setTargetType} showNew={Boolean(analysis.newCustomer)} />
        {isNew && <NewCustomerForm value={newCustomer} onChange={setNewCustomer} proposal={analysis.newCustomer} />}
        {targetType === 'customer' && <CustomerTarget suggestions={analysis.customers} extra={extraCustomer} customerId={customer?.id} onPick={pickCustomer} />}
        {targetType === 'machine' && <MachineTarget suggestions={analysis.machines} machineId={machine?.id} customerId={customer?.id} customerLabel={customer?.label} onPickMachine={pickMachine} onPickCustomer={pickCustomer} />}
      </div>

      {canCreateMachines && (isNew || proposedMachines.length > 0) && (
        <MachinesToCreate
          enabled={createMachines} onToggle={setCreateMachines} items={machineItems} onChange={setMachineItems}
          deadline={deadline} onDeadline={setDeadline} deadlineSource={analysis.deliveryDeadline?.source}
          site={<InstallationSite enabled={siteEnabled} onToggle={setSiteEnabled} value={site} onChange={setSite} fromDocument={Boolean(analysis.installationSite)} />}
        />
      )}

      {targetType === 'machine' && (
        <div>
          <label style={s.label} htmlFor="ai-intake-desc">Popis</label>
          <textarea id="ai-intake-desc" rows={3} style={{ ...s.input, resize: 'vertical' }} value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} />
        </div>
      )}

      <CustomerFill fill={fill} customerLabel={fillCustomer?.label} checked={fieldsChecked} onToggle={toggle} />

      <p style={{ ...s.muted, margin: 0, display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
        <AlertTriangle size={14} color="#F9A825" style={{ flexShrink: 0, marginTop: '2px' }} />
        Návrh připravila AI – před uložením zkontrolujte zákazníka, trenažér i údaje.
      </p>
      <ErrorBox message={error} />
      <div style={s.footer}>
        <Button variant="secondary" onClick={onBack} disabled={saving}>Jiný soubor</Button>
        <Button icon={Check} onClick={save} loading={saving} disabled={!ready}>{isNew ? 'Založit zákazníka a uložit' : 'Uložit dokument'}</Button>
      </div>
    </div>
  );
}

function DoneStep({ done, onAnother, onClose }) {
  const { result, label, type } = done;
  const where = type === 'machine' ? 'k trenažéru' : 'ke kartě zákazníka';
  const machines = result.createdMachines || [];
  const link = { color: '#1976D2', fontWeight: 600 };
  return (
    <div style={{ ...s.body, alignItems: 'center', textAlign: 'center' }}>
      <span style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#E8F5E9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Check size={26} color="#2E7D32" />
      </span>
      <div role="status">
        <p style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 4px' }}>{result.createdCustomer ? 'Zákazník založen a dokument uložen' : 'Dokument uložen'}</p>
        {result.createdCustomer
          ? <p style={{ ...s.muted, margin: 0 }}>Nový zákazník <Link to={`/customers/${result.customerId}`} onClick={onClose} style={link}>{label}</Link>, smlouva je na jeho kartě.</p>
          : <p style={{ ...s.muted, margin: 0 }}>Uložen {where}{label ? ` ${label}` : ''}.</p>}
        {machines.length > 0 && (
          <p style={{ ...s.muted, margin: '6px 0 0' }}>
            Založené trenažéry (smlouva přiložena):{' '}
            {machines.map((m, i) => (
              <span key={m.id}>{i > 0 && ', '}<Link to={`/machines/${m.id}`} onClick={onClose} style={link}>{m.model_type} {m.serial_number}</Link></span>
            ))}
          </p>
        )}
        {machines.length > 0 && result.deliveryDeadline && (
          <p style={{ ...s.muted, margin: '6px 0 0' }}>Doručit do <strong>{formatDeadline(result.deliveryDeadline)}</strong>{result.siteId ? ' · místo instalace uloženo jako provozovna' : ''}.</p>
        )}
        {result.filledFields?.length > 0 && (
          <p style={{ ...s.muted, margin: '4px 0 0' }}>Doplněno u zákazníka: {result.filledFields.map((f) => FILL_FIELDS[f]?.[1] || f).join(', ')}.</p>
        )}
      </div>
      <div style={{ ...s.footer, justifyContent: 'center' }}>
        <Button variant="secondary" icon={Upload} onClick={onAnother}>Nahrát další</Button>
        <Button onClick={onClose}>Zavřít</Button>
      </div>
    </div>
  );
}

function IntakeDialog({ onClose, presetCustomerId, presetMachineId, onSaved }) {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [done, setDone] = useState(null);

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    setError('');
    try {
      setAnalysis(await aiUpload('/documents/analyze', file, { customerId: presetCustomerId, machineId: presetMachineId }));
    } catch (err) {
      setError(err.message || 'Dokument se nepodařilo zpracovat.');
    } finally {
      setAnalyzing(false);
    }
  };
  const reset = () => { setFile(null); setAnalysis(null); setDone(null); setError(''); };
  const saved = (result, label, type) => {
    setDone({ result, label, type });
    if (onSaved) onSaved(result);
  };
  const close = () => { if (!analyzing) onClose(); };

  return (
    // A backdrop click must not throw away a finished (paid) analysis; the X button still closes it.
    <Modal show onClose={analysis && !done ? undefined : close}>
      <SpinKeyframes />
      <div role="dialog" aria-modal="true" aria-labelledby="ai-intake-heading" style={s.panel}>
        <div style={s.header}>
          <Sparkles size={22} color={ACCENT} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="ai-intake-heading" style={{ fontSize: '17px', fontWeight: 700, margin: '0 0 4px' }}>Chytrý příjem dokumentu</h2>
            <span style={s.badgeAi}><Sparkles size={12} /> Zpracuje Claude</span>
          </div>
          <button type="button" onClick={close} disabled={analyzing} aria-label="Zavřít" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '8px', color: '#666' }}>
            <X size={20} />
          </button>
        </div>
        {done && <DoneStep done={done} onAnother={reset} onClose={onClose} />}
        {!done && analysis && <ReviewStep analysis={analysis} onBack={reset} onSaved={saved} />}
        {!done && !analysis && <UploadStep file={file} setFile={setFile} analyzing={analyzing} error={error} onAnalyze={analyze} onClose={close} />}
      </div>
    </Modal>
  );
}

// Mounted only while open, so every opening starts with a clean state.
export default function AiDocumentIntake({ open, onClose, presetCustomerId, presetMachineId, onSaved }) {
  if (!open) return null;
  return <IntakeDialog onClose={onClose} presetCustomerId={presetCustomerId} presetMachineId={presetMachineId} onSaved={onSaved} />;
}
